import React from 'react'
import { UseFormRegister, FieldError, Path, FieldValues } from 'react-hook-form'
import { AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'

interface FormFieldProps<TFieldValues extends FieldValues> {
  label: string
  name: Path<TFieldValues>
  register: UseFormRegister<TFieldValues>
  error?: FieldError
  required?: boolean
  type?: 'text' | 'email' | 'number' | 'date' | 'tel' | 'password'
  placeholder?: string
  disabled?: boolean
  helperText?: string
  className?: string
  min?: number
  max?: number
  step?: number
  rows?: number
  onChange?: (value: string) => void
}

export function FormField<TFieldValues extends FieldValues>({
  label,
  name,
  register,
  error,
  required,
  type = 'text',
  placeholder,
  disabled,
  helperText,
  className,
  min,
  max,
  step,
  onChange,
}: FormFieldProps<TFieldValues>) {
  return (
    <div className={className}>
      <label htmlFor={String(name)} className="block text-sm font-medium text-brand-gray mb-2">
        {label} {required && <span className="text-error">*</span>}
      </label>

      <input
        id={String(name)}
        type={type}
        placeholder={placeholder}
        disabled={disabled}
        min={min}
        max={max}
        step={step}
        className={cn(
          'form-input w-full px-3 py-2 border border-brand-gray/30 rounded-monumenta',
          'bg-brand-dark-lighter text-white placeholder-brand-gray',
          'focus:outline-none focus:ring-2 focus:ring-brand-primary focus:border-brand-primary',
          'hover:border-brand-gray/50 transition-colors duration-200',
          error && 'border-error focus:ring-error focus:border-error'
        )}
        {...register(name, onChange ? {
          onChange: (e) => onChange(e.target.value),
        } as any : undefined)}
      />

      {error && (
        <div className="flex items-center gap-2 mt-2 text-error text-sm">
          <AlertCircle className="w-4 h-4" />
          {error.message}
        </div>
      )}

      {helperText && !error && (
        <p className="text-xs text-brand-gray mt-1">{helperText}</p>
      )}
    </div>
  )
}
